import _ from 'lodash';
import { nanoid } from 'nanoid';

import {
  ALL_LOAN_ROLES_TITLE,
  GLOBAL_LOAN_ROLE_SUFFIX,
  TEMPLATE_VARIABLES_TITLE,
} from 'constants/emailTemplates';
import { ReactRenderingContent } from 'constants/formBuilder';
import { jsonParse, jsonStringify, safeJsonParse } from 'utils/json';
import { logger } from 'utils/logger';

import { getActionProperty } from './forms';

const normalizeTitle = title => {
  return _.snakeCase(title.replace(/[^a-zA-Z0-9\s]/g, ''));
};

const GLOBAL_LOAN_ROLE_SUFFIX_PATTERN = new RegExp(
  `${_.escapeRegExp(GLOBAL_LOAN_ROLE_SUFFIX)}( - |$)`,
);

const stripGlobalLoanRoleSuffix = title => title.replace(GLOBAL_LOAN_ROLE_SUFFIX_PATTERN, '$1');

const getIsLoanRolePath = parentTitle =>
  parentTitle.startsWith(`${normalizeTitle(ALL_LOAN_ROLES_TITLE)}.`);

export const formatDynamicFieldsSchemaToEditorStructure = (schema, withDynamicAction = false) => {
  const previewFieldWrappers = {};
  const legacyMergeTagRewrites = {};

  const formatFields = (fields, parentTitle, legacyParentTitle) =>
    (fields || []).reduce((acc, { title, value, type }) => {
      const isTemplateVariable = parentTitle === normalizeTitle(TEMPLATE_VARIABLES_TITLE);
      const action = withDynamicAction ? getActionProperty({ dataDynamicType: type }) : null;
      const dataDynamicAction = action
        ? `data-dynamic-action="${jsonStringify(action).replace(/"/g, '&quot;')}"`
        : '';

      const fieldHtml = isTemplateVariable
        ? `<span data-template-field-type="${ReactRenderingContent.TEMPLATE_VARIABLE_FIELD}" has-custom-data-attributes="true" data-templateVariableField-type="${type}" data-templateVariableField-name="${value}"></span>`
        : `<span data-template-field-type="${ReactRenderingContent.DYNAMIC_FIELD}" data-dynamic-type="${type}" data-dynamic-value="${value}" data-dynamic-title="${title}" ${dataDynamicAction}>${title}</span>`;

      // escaping needed to preserve &quot that is replaced by _.unescape() on saving email signature
      const fieldWrapper = withDynamicAction ? _.escape(fieldHtml) : fieldHtml;
      previewFieldWrappers[value] = fieldWrapper;

      const normalizedTitle = normalizeTitle(title);
      const isLoanRoleField = getIsLoanRolePath(parentTitle);

      // Loan-role names are user-defined and can collide (e.g. a global and a company "Loan Officer"),
      // so the inserted tag carries the unique field value instead of a title-derived path
      const tagValue = isLoanRoleField
        ? `{{${normalizeTitle(ALL_LOAN_ROLES_TITLE)}.${value}}}`
        : `{{${parentTitle}.${normalizedTitle}}}`;

      if (isLoanRoleField) {
        const legacyTags = _.uniq([
          `{{${parentTitle}.${normalizedTitle}}}`,
          `{{${legacyParentTitle}.${normalizeTitle(stripGlobalLoanRoleSuffix(title))}}}`,
        ]);

        legacyTags
          .filter(legacyTag => !legacyMergeTagRewrites[legacyTag])
          .forEach(legacyTag => {
            legacyMergeTagRewrites[legacyTag] = tagValue;
          });
      }

      acc[value] = {
        name: title,
        value: tagValue,
      };

      return acc;
    }, {});

  const formatCategories = (rootCategories, parentTitle, legacyParentTitle) =>
    (rootCategories || []).reduce((acc, { title, categories, fields }) => {
      const id = nanoid();

      const normalizedTitle = normalizeTitle(title);
      const normalizedLegacyTitle = normalizeTitle(stripGlobalLoanRoleSuffix(title));

      const newParentTitle = parentTitle ? `${parentTitle}.${normalizedTitle}` : normalizedTitle;
      const newLegacyParentTitle = legacyParentTitle
        ? `${legacyParentTitle}.${normalizedLegacyTitle}`
        : normalizedLegacyTitle;

      acc[id] = {
        name: title,
        mergeTags: {
          ...formatFields(fields, newParentTitle, newLegacyParentTitle),
          ...formatCategories(categories, newParentTitle, newLegacyParentTitle),
        },
      };

      return acc;
    }, {});

  return {
    previewFieldWrappers,
    legacyMergeTagRewrites,
    formattedCategories: formatCategories(schema),
  };
};

// Tags are spliced into the serialized design JSON, so both sides are escaped for a
// JSON-string context — an unexpected character in a schema value must not corrupt the blob
const escapeForJsonString = value => jsonStringify(value).slice(1, -1);

export const applyLegacyMergeTagRewrites = (metadata, legacyMergeTagRewrites) =>
  Object.entries(legacyMergeTagRewrites).reduce((acc, [legacyTag, tagValue]) => {
    const escapedLegacyTag = escapeForJsonString(legacyTag);

    return acc.includes(escapedLegacyTag)
      ? acc.split(escapedLegacyTag).join(escapeForJsonString(tagValue))
      : acc;
  }, metadata);

export const parseDesignWithLegacyRewrites = (metadata, legacyMergeTagRewrites) => {
  try {
    return {
      design: jsonParse(applyLegacyMergeTagRewrites(metadata, legacyMergeTagRewrites)),
      isRewriteApplied: true,
    };
  } catch (rewriteError) {
    // a failed rewrite must never cost the user their saved design — fall back to the original
    // blob, and report isRewriteApplied: false so the caller can retry the migration later
    logger.error(rewriteError);

    const design = safeJsonParse(metadata);

    if (design === null) {
      logger.error(new Error('Email design metadata is not parseable JSON'));
    }

    return { design, isRewriteApplied: false };
  }
};

const createTemplateVariableTitle = name => name.replace(/([a-z])([A-Z])/g, '$1 $2');

export const mapTemplateVariables = data => {
  const mappedFields = data.map(({ type, name }) => ({
    type,
    title: createTemplateVariableTitle(name),
    value: name,
  }));

  return data.length ? [{ title: TEMPLATE_VARIABLES_TITLE, fields: mappedFields }] : [];
};

export const formatSpecialLinks = specialLinks => {
  return _.map(specialLinks, specialLink => ({
    name: specialLink.name,
    href: specialLink.linkTemplate,
    target: specialLink.target === 'SameTab' ? '_self' : '_blank',
  }));
};
